import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./BothKeysPaywall.module.scss"

/**
 * BothKeysPaywall (`/bothkeys`) — an original, self-contained ONBOARDING paywall
 * for Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md. Concept doc: claude-paywalls/both-keys.md.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * Newest @Paywalls-Bot mention this run: `focus on security and privacy` (NOT the
 * reset keyword `default`) → an ACTIVE STANDING DIRECTIVE at super-high priority.
 * The whole concept is built to it — told as DUAL CONTROL: two keys must turn.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "It takes two keys. Both are yours." Your vault has a dual-control lock — like
 * the ones on a launch console or a bank's inner door, it opens only when TWO
 * keys turn together. And here both keys belong to you: KEY ONE is *this device*
 * (something you have); KEY TWO is *your Face ID or private PIN* (something only
 * you are). No one outside holds either — not the cloud, not another app, not
 * even a friend holding your unlocked phone. The memories are shown in full
 * colour the WHOLE time on purpose: nothing is ever hidden from you — the drama
 * is who else can reach in, and the honest answer is no one.
 *
 * You take your key and TURN it (a rotary drag); the device key turns with you as
 * it recognises your face; the deadbolt throws across the plate; a SECURED · 2/2
 * seal latches; and every outsider is stamped "no key". A scripted turn auto-runs
 * for passive viewers; reduced motion skips to the secured state.
 *
 * ── Divergence (vs. the mined security/privacy cluster) ─────────────────────
 * Layout   : a modern SECURE CONSOLE — a lock plate holding a small stack of
 *            memories, flanked by two keyways + a live 0/2→2/2 readout. Not a
 *            brass safe-deposit box (Strongbox), not a round vault handwheel door
 *            (VaultDoor), not a scatter page (Ink), not a film strip (Negative),
 *            not a redacted contact sheet (Cleared).
 * Interact : DUAL-KEY TURN — you rotate YOUR key; the device key turns in sync;
 *            the bolt throws only when both complete. New to the set: no reveal,
 *            no seat-a-tray slide, no single handwheel crank.
 * Value    : DUAL CONTROL / risk reversal — two independent keys, both yours, and
 *            an explicit "who else holds a key? no one" ledger. Not visibility.
 * Register : engineered, precise, reassuring (secure-console) — not brass-bank
 *            ceremony, not intimate invisible-ink wonder, not wry officialdom.
 *
 * Honest: maps to real protections (its own Face ID / PIN lock, separate from the
 * phone's; kept out of the camera roll & shared albums; private encrypted cloud
 * backup). The two keys are a real 2-factor truth (a device you have + a face/PIN
 * only you are). Strictly additive — nothing is moved, deleted, or lost, and
 * access never expires. No fake urgency/scarcity. Yearly is the featured plan.
 */

// The memories inside the plate — reuse existing personal-memory photos. Shown
// in full colour throughout; the point is who can *reach* them, never hiding.
const TILES = ["m01", "m06", "m10", "m05"]

// Who else holds a key to your vault? Each is a real party the vault shuts out.
// Stamped "no key" as the bolt throws.
const OUTSIDE = [
  { k: "cloud", label: "Cloud sync" },
  { k: "shared", label: "Shared albums" },
  { k: "apps", label: "Other apps" },
  { k: "borrow", label: "A borrowed phone" },
]

// The two keys — both belong to you. A genuine two-factor pair.
const KEYS = [
  { id: "device", label: "This device", note: "something you have" },
  { id: "you", label: "Your Face ID or PIN", note: "something only you are" },
]

// What your two keys actually protect — real product features, stated plainly.
const PROTECTS = [
  {
    k: "lock",
    label: "Its own Face ID or PIN lock — separate from your phone's",
  },
  { k: "roll", label: "Kept out of your camera roll & shared albums" },
  { k: "cloud", label: "Private, encrypted cloud backup" },
]

type PlanKey = "yearly" | "monthly"

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)

const BothKeysPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  // turn ∈ 0..1 : how far YOUR key has turned. secured latches at the end.
  const [turn, setTurn] = useState<number>(() => (reduceMotion ? 1 : 0))
  const [secured, setSecured] = useState<boolean>(!!reduceMotion)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)
  const [hintNudge, setHintNudge] = useState(false)

  const keyRef = useRef<HTMLButtonElement>(null)
  const touched = useRef(false)
  const securedRef = useRef(!!reduceMotion)
  const rafRef = useRef<number>(0)
  const dragging = useRef(false)
  const lastAngle = useRef<number | null>(null)
  const accum = useRef<number>(0) // accumulated turn in "sweeps" (0..1)
  const timers = useRef<number[]>([])
  const pushTimer = (id: number) => timers.current.push(id)

  const stopRaf = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = 0
  }

  // Latch the vault: both keys turned, deadbolt thrown, outsiders stamped out.
  const secure = useCallback(() => {
    if (securedRef.current) return
    securedRef.current = true
    touched.current = true
    stopRaf()
    dragging.current = false
    accum.current = 1
    setTurn(1)
    setSecured(true)
    native.logMixpanelEvent("html_bothkeys_secured")
  }, [])

  // Smoothly drive the turn from its current value up to 1, then latch.
  const animateToSecure = useCallback(() => {
    if (securedRef.current) return
    touched.current = true
    stopRaf()
    const from = accum.current
    const DUR = 620
    let start = 0
    const step = (t: number) => {
      if (securedRef.current) return
      if (!start) start = t
      const p = clamp01((t - start) / DUR)
      // easeInOutCubic
      const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2
      const v = from + (1 - from) * e
      accum.current = v
      setTurn(v)
      if (p >= 1) {
        secure()
        return
      }
      rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
  }, [secure])

  // Auto-demo for passive viewers: after a few idle seconds, turn the key.
  useEffect(() => {
    if (reduceMotion) {
      setShowClose(true)
      return
    }
    pushTimer(window.setTimeout(() => setShowClose(true), 2200))
    pushTimer(window.setTimeout(() => setHintNudge(true), 1500))
    pushTimer(
      window.setTimeout(() => {
        if (!touched.current && !securedRef.current) {
          native.logMixpanelEvent("html_bothkeys_autodemo")
          animateToSecure()
        }
      }, 2900),
    )
    return () => {
      stopRaf()
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- rotary drag on YOUR key ------------------------------------------------
  const angleTo = (clientX: number, clientY: number) => {
    const el = keyRef.current
    if (!el) return 0
    const r = el.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    return Math.atan2(clientY - cy, clientX - cx) * (180 / Math.PI)
  }

  const beginDrag = (clientX: number, clientY: number) => {
    if (securedRef.current || reduceMotion) return
    dragging.current = true
    touched.current = true
    setHintNudge(false)
    stopRaf()
    lastAngle.current = angleTo(clientX, clientY)
  }

  const moveDrag = (clientX: number, clientY: number) => {
    if (!dragging.current || securedRef.current) return
    const a = angleTo(clientX, clientY)
    if (lastAngle.current == null) {
      lastAngle.current = a
      return
    }
    let delta = a - lastAngle.current
    if (delta > 180) delta -= 360
    if (delta < -180) delta += 360
    lastAngle.current = a
    // a full key turn is ~120° of wrist rotation
    accum.current = clamp01(accum.current + delta / 120)
    setTurn(accum.current)
    if (accum.current >= 0.94) secure()
  }

  const endDrag = () => {
    if (securedRef.current) return
    dragging.current = false
    lastAngle.current = null
    // not far enough — ease back so it's clear the lock needs a full commit
    if (accum.current < 0.94) {
      const from = accum.current
      const DUR = 260
      let start = 0
      const step = (t: number) => {
        if (securedRef.current || dragging.current) return
        if (!start) start = t
        const p = clamp01((t - start) / DUR)
        const v = from * (1 - p)
        accum.current = v
        setTurn(v)
        if (p < 1) rafRef.current = requestAnimationFrame(step)
      }
      rafRef.current = requestAnimationFrame(step)
    }
  }

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    beginDrag(e.clientX, e.clientY)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (e.buttons === 0) return
    moveDrag(e.clientX, e.clientY)
  }
  const onKeyActivate = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      animateToSecure()
    }
  }

  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct
  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    native.logMixpanelEvent("html_bothkeys_subscribe", { plan })
    if (selectedProduct) native.subscribe(selectedProduct)
  }
  const openTerms = () => native.postNativeEvent("terms")
  const openPrivacy = () => native.postNativeEvent("privacy")

  // derived visuals
  const yourDeg = turn * 90 // your key rotates up to 90°
  const deviceDeg = turn * 90 // device key turns in sympathy (recognises you)
  const boltPct = Math.round(turn * 100)
  const keysDone = secured ? 2 : turn > 0.5 ? 1 : 0

  return (
    <div className={`${styles.root} ${secured ? styles.rootSecured : ""}`}>
      <div className={styles.field} aria-hidden="true" />

      {/* ---------- header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="11.4" r="2.1" fill="currentColor" />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- title ---------- */}
      <div className={styles.intro}>
        <span className={styles.eyebrow}>Two keys. Both yours.</span>
        <h1 className={styles.title}>
          It takes two keys
          <br />
          to open your vault.
        </h1>
        <p className={styles.sub}>
          Your memories stay in full colour the whole time — nothing is ever
          hidden from you. The lock decides who else can reach in. It answers to
          two keys, and you hold them both.
        </p>
      </div>

      {/* ---------- the dual-control console ---------- */}
      <div
        className={styles.console}
        style={{ ["--turn" as any]: turn }}
        role="group"
        aria-label="Dual-control lock"
      >
        <div className={styles.consoleTop}>
          <span className={styles.readout}>
            <span className={styles.readoutDot} aria-hidden="true" />
            {secured ? "SECURED" : "AWAITING KEYS"}
          </span>
          <span className={styles.keysCount} aria-live="polite">
            {keysDone}
            <span>/2 KEYS</span>
          </span>
        </div>

        {/* the plate that holds your memories */}
        <div className={styles.plate}>
          <div className={styles.tiles} aria-hidden="true">
            {TILES.map((id, i) => (
              <span
                key={id}
                className={styles.tile}
                style={{
                  backgroundImage: `url(/byheart/${id}.jpg)`,
                  ["--i" as any]: i,
                }}
              />
            ))}
          </div>

          {/* faceted secure-glass that seats over the memories when locked */}
          <div className={styles.glass} aria-hidden="true" />

          {/* the deadbolt: throws across the plate and plugs into the strike */}
          <div className={styles.boltTrack} aria-hidden="true">
            <span className={styles.boltHousing} />
            <div className={styles.bolt}>
              <span className={styles.boltBar} />
              <span className={styles.boltCap} />
            </div>
            <div className={styles.strike}>
              <span className={styles.strikeSlot} />
            </div>
          </div>

          <motion.div
            className={styles.stamp}
            aria-hidden={!secured}
            initial={false}
            animate={
              secured
                ? { opacity: 1, scale: 1, rotate: -8 }
                : { opacity: 0, scale: 1.55, rotate: -22 }
            }
            transition={{ type: "spring", stiffness: 340, damping: 18 }}
          >
            <span className={styles.stampInner}>
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                aria-hidden="true"
              >
                <path
                  d="M6 11V8a6 6 0 0 1 12 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <rect
                  x="4.6"
                  y="11"
                  width="14.8"
                  height="9.4"
                  rx="2.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                />
              </svg>
              <em>
                LOCKED
                <span>2 KEYS · BOTH YOURS</span>
              </em>
            </span>
          </motion.div>
        </div>

        {/* the two keyways */}
        <div className={styles.keys}>
          {KEYS.map((k, idx) => {
            const isYou = k.id === "you"
            const deg = isYou ? yourDeg : deviceDeg
            const on = secured || turn > (isYou ? 0.5 : 0.15)
            const KeyGlyph = (
              <span
                className={styles.keyGlyph}
                style={{ transform: `rotate(${deg}deg)` }}
                aria-hidden="true"
              >
                <svg viewBox="0 0 40 40" width="46" height="46">
                  <circle
                    cx="20"
                    cy="12"
                    r="6.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  />
                  <circle cx="20" cy="12" r="2.2" fill="currentColor" />
                  <path
                    d="M20 18.6v14"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <path
                    d="M20 27h4.4M20 30.5h3.2"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            )
            return (
              <div
                key={k.id}
                className={`${styles.keySlot} ${on ? styles.keySlotOn : ""} ${
                  isYou ? styles.keySlotYou : ""
                }`}
              >
                <span className={styles.keyRing}>
                  {isYou && !secured && !reduceMotion ? (
                    <button
                      ref={keyRef}
                      className={`${styles.keyTurn} ${
                        hintNudge ? styles.keyNudge : ""
                      }`}
                      onPointerDown={onPointerDown}
                      onPointerMove={onPointerMove}
                      onPointerUp={endDrag}
                      onPointerCancel={endDrag}
                      onPointerLeave={endDrag}
                      onKeyDown={onKeyActivate}
                      aria-label="Turn your key to secure the vault"
                    >
                      {KeyGlyph}
                    </button>
                  ) : (
                    KeyGlyph
                  )}
                  <span className={styles.keyGleam} aria-hidden="true" />
                </span>
                <span className={styles.keyMeta}>
                  <span className={styles.keyLabel}>
                    {on && (
                      <svg
                        viewBox="0 0 24 24"
                        width="11"
                        height="11"
                        aria-hidden="true"
                      >
                        <path
                          d="M5 12.5l4 4 10-10.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {k.label}
                  </span>
                  <span className={styles.keyNote}>{k.note}</span>
                </span>
              </div>
            )
          })}
          <span className={styles.keysBridge} aria-hidden="true" />
        </div>
      </div>

      {/* ---------- turn prompt / honesty ---------- */}
      <div className={styles.hint} aria-live="polite">
        {secured ? (
          <span className={styles.hintDone}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Both keys turned — your vault opens for you alone
          </span>
        ) : (
          <button
            className={styles.turnBtn}
            onClick={animateToSecure}
            disabled={!!reduceMotion}
          >
            <span className={styles.turnBtnDot} aria-hidden="true" />
            Turn your key
          </button>
        )}
        <span className={styles.honest}>
          Nothing is moved or deleted — the lock only decides who may open it.
        </span>
      </div>

      {/* ---------- who else holds a key? ---------- */}
      <div className={styles.outside}>
        <span className={styles.outsideCap}>Who else holds a key?</span>
        <div className={styles.chips}>
          {OUTSIDE.map((o, i) => (
            <span
              key={o.k}
              className={`${styles.chip} ${secured ? styles.chipOn : ""}`}
              style={{ transitionDelay: `${140 + i * 80}ms` }}
            >
              <svg
                viewBox="0 0 24 24"
                width="11"
                height="11"
                aria-hidden="true"
              >
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                />
              </svg>
              {o.label}
              <em>no key</em>
            </span>
          ))}
        </div>
      </div>

      {/* ---------- protections ---------- */}
      <div className={styles.protects}>
        {PROTECTS.map((p, i) => (
          <span
            key={p.k}
            className={`${styles.protect} ${secured ? styles.protectOn : ""}`}
            style={{ transitionDelay: `${180 + i * 90}ms` }}
          >
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {p.label}
          </span>
        ))}
      </div>

      {/* ---------- pricing (yearly featured) ---------- */}
      <div className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbon}>SAVE {savingsPct}%</span>
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planMeta}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                7 days free · then {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planRight}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planName}>Monthly</span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={handleSubscribe}>
        <span className={styles.ctaMain}>
          {plan === "yearly" ? "Try 7 days free" : "Continue"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `Then ${yearlyPrice}/year · cancel anytime`
            : `${monthlyPrice}/month · cancel anytime`}
        </span>
      </button>

      <div className={styles.legal}>
        <button onClick={openTerms}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={openPrivacy}>Privacy</button>
        <span aria-hidden="true">·</span>
        <span>Auto-renews until cancelled</span>
      </div>
    </div>
  )
}

export default BothKeysPaywall
